import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Review from '@components/ui/Review';
import { useAppSelector } from '@hooks/redux';
import { useTranslation } from 'react-i18next';
import { CloseOutlined, FormOutlined, MessageOutlined, QuestionCircleOutlined } from '@ant-design/icons';

declare global {
  interface Window {
    jivo_api?: {
      open: () => void;
    };
  }
}

const SupportButton = () => {
  const [open, setOpen] = useState(false);
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const navigate = useNavigate();
  const isAuth = useAppSelector(state => state.appReducer.isAuth);
  const { t } = useTranslation();

  return (
    <>
      {open && <div className='fixed inset-0 z-[9998]' onClick={() => setOpen(false)} />}

      <Review modalOpen={isReviewOpen} onModalClose={() => setIsReviewOpen(false)} />

      <div className={`fixed bottom-6 right-6 z-[9999] ${isReviewOpen ? 'hidden' : ''}`}>
        <div
          className={`absolute bottom-full right-0 mb-3 bg-white rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 w-[350px] ${
            open ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-4 pointer-events-none'
          }`}
        >
          <div className='px-5 py-4 border-b border-gray-100'>
            <p className='text-sm font-semibold text-gray-500 uppercase tracking-wide'>{t('Support service')}</p>
          </div>

          <div className='p-3 flex flex-col gap-2'>
            <button
              onClick={() => {
                window.jivo_api?.open();
                setOpen(false);
              }}
              className='w-full flex items-center gap-4 bg-gray-50 hover:bg-[#fff8e7] border-0 rounded-xl px-5 py-4 cursor-pointer transition-colors duration-200 text-left'
            >
              <div className='w-10 h-10 rounded-full bg-[#ffb700] flex items-center justify-center flex-shrink-0'>
                <MessageOutlined className='text-white text-base' />
              </div>
              <div>
                <p className='text-lg font-semibold text-gray-800'>{t('Write to chat')}</p>
                <p className='text-base text-gray-400 mt-0.5'>{t('Online support')}</p>
              </div>
            </button>

            {isAuth && (
              <>
                <button
                  onClick={() => {
                    navigate('/faq');
                    setOpen(false);
                  }}
                  className='w-full flex items-center gap-4 bg-gray-50 hover:bg-[#fff8e7] border-0 rounded-xl px-5 py-4 cursor-pointer transition-colors duration-200 text-left'
                >
                  <div className='w-10 h-10 rounded-full bg-[#ffb700] flex items-center justify-center flex-shrink-0'>
                    <QuestionCircleOutlined className='text-white text-base' />
                  </div>
                  <div>
                    <p className='text-lg font-semibold text-gray-800'>FAQ</p>
                    <p className='text-base text-gray-400 mt-0.5'>{t('Frequently asked questions')}</p>
                  </div>
                </button>

                <button
                  onClick={() => {
                    setIsReviewOpen(true);
                    setOpen(false);
                  }}
                  className='w-full flex items-center gap-4 bg-gray-50 hover:bg-[#fff8e7] border-0 rounded-xl px-5 py-4 cursor-pointer transition-colors duration-200 text-left'
                >
                  <div className='w-10 h-10 rounded-full bg-[#ffb700] flex items-center justify-center flex-shrink-0'>
                    <FormOutlined className='text-white text-base' />
                  </div>
                  <div>
                    <p className='text-lg font-semibold text-gray-800'>{t('Leave a review')}</p>
                    <p className='text-base text-gray-400 mt-0.5'>{t('Your feedback helps us')}</p>
                  </div>
                </button>
              </>
            )}
          </div>
        </div>

        <button
          onClick={() => (isAuth ? setOpen(prev => !prev) : window.jivo_api?.open())}
          className='w-16 h-16 rounded-full bg-[#ffb700] border-none cursor-pointer flex items-center justify-center shadow-xl hover:scale-110 transition-all duration-300'
        >
          {open ? (
            <CloseOutlined className='text-2xl text-white' />
          ) : (
            <MessageOutlined className='text-3xl text-white' />
          )}
        </button>
      </div>
    </>
  );
};

export default SupportButton;
